import { useCallback } from 'react';
import { useIdentity } from './useIdentity';

/**
 * Per-room Beam transport accessor.
 *
 * Reads the persisted transport pick for the current room (from
 * `useIdentity().partyCode`) and returns a `[value, setValue]` tuple in the
 * same shape as `useState`. Rooms without an explicit pick default to
 * `'cloud'` — F10.2's sameLAN integration in a later wave can promote a fresh
 * room to `'lan'` automatically; until then Cloud is the safe default that
 * works regardless of peer reachability.
 *
 * `setValue(next)` writes through to the IdentityProvider, which persists to
 * localStorage and syncs across tabs via the storage event.
 *
 * @returns {['lan' | 'cloud', (next: 'lan' | 'cloud') => void]}
 */
export function useTransport() {
  const { transportByRoom, partyCode, setTransport } = useIdentity();
  const value = transportByRoom[partyCode] ?? 'cloud';
  const setValue = useCallback(
    (next) => setTransport(partyCode, next),
    [setTransport, partyCode],
  );
  return [value, setValue];
}

export default useTransport;
